import React, { useState, useEffect } from 'react';
import { AdminTopNav, AdminSideNav } from '../administrationNavigation/AdminConsoleNavigation';
import GetCookie from '../../utils/GetCookie'
import ErrorPopup from '../../utils/popup/ErrorPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import TrimContent from '../../utils/TrimContent';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PaginationControls from '../../utils/PaginationControls';
import { useTimezone } from '../../utils/TimezoneContext';

const AdminPermissionSetManagement = () => {
  const navigate = useNavigate();
  const accessToken = GetCookie('access_token');
  const { formatDateTime } = useTimezone();

  // State variables for permission sets listing
  const [permissionSets, setPermissionSets] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(25);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortByActive, setSortByActive] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // State variables for form management
  const [showForm, setShowForm] = useState(false);
  const [editingSet, setEditingSet] = useState(null);
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [availableRoutes, setAvailableRoutes] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // Delete state - updated for reusable modal
  const [deleteSet, setDeleteSet] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  // API base URL
  const API_BASE_URL = '/pceapi/administration';

  // HTTP headers with authorization
  const headers = {
    Authorization: accessToken
  };

  // Fetch permission sets on component mount and when dependencies change
  useEffect(() => {
    fetchPermissionSets();
  }, [page, size, search, sortByActive, refreshTrigger]);

  // Initialize searchInput with search value
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  // Fetch available routes when form is shown
  useEffect(() => {
    if (showForm) {
      fetchAvailableRoutes();
    }
  }, [showForm]);

  // Load initial data if editing
  useEffect(() => {
    if (editingSet) {
      setName(editingSet.name || '');
      setIsActive(editingSet.is_active || true);
      setSelectedPermissions(editingSet.permissions || []);
    } else {
      setName('');
      setIsActive(true);
      setSelectedPermissions([]);
    }
  }, [editingSet]);

  // Fetch permission sets with pagination, search, and sorting
  const fetchPermissionSets = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading permission sets');
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/permission-set/listsets`, 
        {
          page,
          size,
          search: search || undefined,
          sort_by_active: sortByActive
        },
        { headers }
      );
      
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        navigate('/admin-login');
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load permission sets');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPermissionSets(data.data || []);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching permission sets:', error);
      setPopupErrorMessage(error.message || 'Failed to load permission sets');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Fetch available API routes for permission selection
  const fetchAvailableRoutes = async () => {
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Loading available routes');
    
    try {
      const response = await axios.get(
        `${API_BASE_URL}/permission-set/routes`,
        { headers }
      );
      const data = response.data;
      
      if (data.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        navigate('/admin-login');
        return;
      }
      
      if (data.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(data.message || 'Failed to load available routes');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setAvailableRoutes(data.data || {});
      
      // Initialize expanded state for all root groups
      const initialExpandedState = {};
      Object.keys(data.data || {}).forEach(key => {
        initialExpandedState[key] = false;
      });
      setExpandedGroups(initialExpandedState);
    } catch (error) {
      console.error('Error fetching routes:', error);
      setPopupErrorMessage(error.message || 'Failed to load available routes');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!name.trim()) {
      setPopupErrorMessage('Name is required');
      setIsErrorPopupVisible(true);
      return;
    }
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage(editingSet ? 'Updating permission set' : 'Creating permission set');
    
    try {
      const formData = {
        name: name.trim(),
        permissions: selectedPermissions,
        is_active: isActive
      };
      
      let response;
      if (editingSet) {
        response = await axios.post(
          `${API_BASE_URL}/permission-set/update/${editingSet.id}`, 
          formData,
          { headers }
        );
      } else {
        response = await axios.post(
          `${API_BASE_URL}/permission-set/create`, 
          formData,
          { headers }
        );
      }
      
      const result = response.data;
      
      if (result.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message);
        setIsErrorPopupVisible(true);
        return;
      }
      
      if (result.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message || 'Operation failed');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(result.message || 'Operation completed successfully');
      setIsSuccessPopupVisible(true);
      
      // Reset form and fetch updated data
      setShowForm(false);
      setEditingSet(null);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Submit error:', error);
      setPopupErrorMessage(error.message || 'An error occurred');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
    }
  };

  // Delete handler - updated for reusable modal
  const handleDelete = async () => {
    if (!deleteSet) return;
    
    setIsProcessingPopupVisible(true);
    setProcessingMessage('Deleting permission set');
    setShowDeleteConfirm(false);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/permission-set/delete/${deleteSet.id}`,
        {},
        { headers }
      );
      
      const result = response.data;
      
      if (result.status === 'NotAuthorized') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message);
        setIsErrorPopupVisible(true);
        return;
      }
      
      if (result.status === 'failed') {
        setIsProcessingPopupVisible(false);
        setPopupErrorMessage(result.message || 'Failed to delete permission set');
        setIsErrorPopupVisible(true);
        return;
      }
      
      setPopupSuccessMessage(result.message || 'Permission set deleted successfully');
      setIsSuccessPopupVisible(true);
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Delete error:', error);
      setPopupErrorMessage(error.message || 'Failed to delete permission set');
      setIsErrorPopupVisible(true);
    } finally {
      setIsProcessingPopupVisible(false);
      setDeleteSet(null);
    }
  };

  // Toggle expanded state for a group
  const toggleGroupExpand = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  };

  // Check if permission is selected
  const isPermissionSelected = (path) => {
    return selectedPermissions.includes(path);
  };

  // Toggle permission selection
  const togglePermission = (path) => {
    if (isPermissionSelected(path)) {
      setSelectedPermissions(prev => prev.filter(p => p !== path));
    } else {
      setSelectedPermissions(prev => [...prev, path]);
    }
  };

  // Get all paths from a nested routes object
  const getAllPaths = (routes) => {
    let paths = [];
    
    Object.entries(routes).forEach(([key, value]) => {
      if (value.isAction) {
        paths.push(value.path);
      } else {
        paths = [...paths, ...getAllPaths(value)];
      }
    });
    
    return paths;
  };

  // Check if all children of a group are selected
  const areAllChildrenSelected = (group, routes) => {
    const allPaths = getAllPaths(routes);
    return allPaths.every(path => isPermissionSelected(path));
  };

  // Toggle selection for all children of a group
  const toggleGroupSelection = (group, routes) => {
    const allPaths = getAllPaths(routes);
    
    if (areAllChildrenSelected(group, routes)) {
      // Deselect all
      setSelectedPermissions(prev => prev.filter(path => !allPaths.includes(path)));
    } else {
      // Select all
      setSelectedPermissions(prev => {
        const newPaths = allPaths.filter(path => !prev.includes(path));
        return [...prev, ...newPaths];
      });
    }
  };

  // Open form for editing a permission set
  const handleOpenEditForm = (permissionSet) => {
    setEditingSet(permissionSet);
    setShowForm(true);
  };

  // Open delete confirmation dialog - updated for reusable modal
  const handleOpenDeleteConfirm = (set) => {
    setDeleteSet(set);
    setShowDeleteConfirm(true);
  };

  // Handle search input change
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };
  
  // Handle search button click
  const handleSearch = () => {
    setSearch(searchInput);
    setPage(1);
  };
  
  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Popup close handlers
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
  };

  // Render route hierarchy recursively
  const renderRouteHierarchy = (routes, prefix = '') => {
    return (
      <div>
        {Object.entries(routes).map(([key, value]) => {
          const fullPath = prefix ? `${prefix}.${key}` : key;
          
          if (value.isAction) {
            // Render action/endpoint
            return (
              <div key={value.path} className="ml-3" style={{marginLeft: prefix.split('.').length * 15 + 15 + 'px'}}>
                <label className="d-flex align-items-center ev-reg-p-dark">
                  <input
                    type="checkbox"
                    checked={isPermissionSelected(value.path)}
                    onChange={() => togglePermission(value.path)}
                    className="form-check-input me-2"
                  />
                  <span className="text-nowrap">
                    {key.replace(/\{.*\}/, '*')}
                  </span>
                </label>
                <small className="ev-small-p-blue d-block mb-2 ms-4">{value.path}</small>
              </div>
            );
          } else {
            // Render group/folder
            const isExpanded = expandedGroups[fullPath];
            const childPaths = getAllPaths(value);
            const allSelected = childPaths.length > 0 && areAllChildrenSelected(fullPath, value);
            const someSelected = childPaths.some(path => isPermissionSelected(path));
            
            return (
              <div key={fullPath} style={{marginLeft: prefix.split('.').length * 15 + 'px'}}>
                <div 
                  className="d-flex align-items-center mb-2 mt-2 ev-reg-p-blue" 
                >
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={input => {
                      if (input) {
                        input.indeterminate = !allSelected && someSelected;
                      }
                    }}
                    onChange={() => toggleGroupSelection(fullPath, value)}
                    className="form-check-input me-2"
                  />
                  <span onClick={() => toggleGroupExpand(fullPath)} className="flex-grow-1">
                    {key}
                  </span>
                  <i 
                    className={`bi ${isExpanded ? 'bi-folder2-open text-warning' : 'bi-folder2'} me-2`}
                    onClick={() => toggleGroupExpand(fullPath)}
                  ></i>
                  <i 
                    className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'}`}
                    onClick={() => toggleGroupExpand(fullPath)}
                  ></i>
                </div>
                {isExpanded && (
                  <div className="ms-2">
                    {renderRouteHierarchy(value, fullPath)}
                  </div>
                )}
              </div>
            );
          }
        })}
      </div>
    );
  };

  return (
    <div className="main-window">
      <AdminTopNav />
      <div className="container-fluid">
        <div className="row">
          <AdminSideNav />
          <main className="col-12 col-md-9 col-lg-10 ms-sm-auto console-main-window">
            <div className="mx-5 pb-5">
              <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center mb-0 margin-top-4">
                <p className="ev-med-p-blue mb-1">Permission Sets <span className="ev-light-reg">({total})</span></p>
                {!showForm && ( 
                  <button 
                    className="ev-btn-yellow-fill-round ev-btn-med ev-bold-reg"  
                    onClick={() => {
                      setEditingSet(null);
                      setName('');
                      setIsActive(true);
                      setSelectedPermissions([]);
                      setShowForm(true);
                    }}
                  >
                    <i className="bi bi-plus-circle me-1"></i> Create Permission Set
                  </button>
                )}
              </div>
              <p className="ev-message-card2 mb-4">Access levels for users on Evaior are defined by permission sets, which appear as available roles in the Admin portal.</p>

              {/* Show form if in edit/create mode, otherwise show list */}
              {showForm ? (
                <div className="ev-card-2 w-100 ev-padding-xlarge">
                  <p className="ev-med-p-blue mb-4">
                    {editingSet ? 'Edit Permission Set' : 'Create Permission Set'}
                  </p>
                  
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-group mb-3">
                          <label className="ev-reg-p-dark mb-1">Permission Set Name *</label>
                          <input type="text" className="formField3" value={name} onChange={(e) => setName(e.target.value)} required/>
                        </div>
                        
                        <div className="form-check form-switch mb-4">
                          <input className="form-check-input" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} id="activeSwitch"/>
                          <label className="ev-reg-p-dark" htmlFor="activeSwitch">Enabled</label>
                        </div>
                        
                        <div className="mb-4">
                          <label className="ev-reg-p-dark mb-1">Selected Permissions ({selectedPermissions.length})</label>
                          <div className="ev-card-7 ev-radius-small scrollable-element vh-35">
                            {selectedPermissions.length === 0 ? (
                              <p className="m-1 ev-small-p-dark">No permissions selected</p>
                            ) : (
                              selectedPermissions.map((path) => (
                                <div key={path} className="text-truncate ev-small-p-dark m-1">
                                  {path}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-4 text-end">
                          <button type="submit" className="ev-btn-blue-fill-round ev-bold-reg" ><i className="bi bi-arrow-down-circle"></i> Save Set</button> <span>&nbsp;</span>
                          <button type="button" className="ev-btn-blue-outline-round ev-bold-reg ms-2" onClick={() => setShowForm(false)}>Cancel</button>
                        </div>
                      </div>
                      
                      <div className="col-md-8">
                        <label className="ev-reg-p-dark mb-1">Select Permissions</label>
                        <div className="ev-card-3 scrollable-element vh-55">
                          {Object.keys(availableRoutes).length === 0 ? (
                            <p className="text-muted ev-small-p-dark">
                              {isProcessingPopupVisible ? 'Loading routes..' : 'No routes available'}
                            </p>
                          ) : (
                            renderRouteHierarchy(availableRoutes)
                          )}
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              ) : (
                /* Permission Sets List */
                <div className="ev-card-4 w-100 ev-padding-large">
                  <div className="row mt-1 mb-0">
                    <div className="col-6 position-relative">
                      <input
                        type="text"
                        className="form-control search-input ms-0"
                        placeholder="Search by name or permission"
                        value={searchInput}
                        onChange={handleSearchInputChange}
                        onKeyPress={handleSearchKeyPress}
                      />
                      <button 
                        className="ev-btn-lightblue-fill-round search-bar-button" 
                        onClick={handleSearch}
                      >
                        <i className="bi bi-search"></i>
                      </button>
                    </div>
                    
                    <div className="col-6 text-end">
                      <button 
                        className="ev-btn-blue-outline-round ev-bold-reg ms-2" 
                        onClick={() => setSortByActive(sortByActive === null ? true : sortByActive === true ? false : null)}
                      >
                        {sortByActive === null ? 'Sort by Active' : 
                        sortByActive === true ? 'Active First' : 'Inactive First'}
                        {sortByActive !== null && (
                          <i className={`bi ${sortByActive ? 'bi-arrow-up' : 'bi-arrow-down'}`}></i>
                        )}
                      </button>
                      
                      <button 
                        className="ev-btn-lightblue-fill-round ev-bold-reg ms-2" 
                        onClick={() => setRefreshTrigger(prev => prev + 1)}
                      ><i className="bi bi-arrow-repeat"></i> Refresh 
                      </button>
                    </div>
                  </div>

                  <div className="table-container">
                    <table className="table-custom1 mt-0 overflow-hidden w-100">
                      <thead>
                        <tr>
                          <th className="w-20">
                            <label>Name</label>
                          </th>
                          <th className="w-15">
                            <label>Permission Sets</label>
                          </th>
                          <th className="w-10">
                            <label>Status</label>
                          </th>
                          <th className="w-15">
                            <label>Created by</label>
                          </th>
                          <th className="w-15">
                            <label>Created at</label>
                          </th>
                          <th className="w-15">
                            <label>Updated at</label>
                          </th>
                          <th className="w-10">
                            <label>Actions</label>
                          </th>
                        </tr>
                      </thead>
                    </table>

                    <div className="scrollable-element dashboard-right-contentarea-table-height-1">
                      <table className="table-custom1 overflow-hidden w-100">
                        <tbody>
                          {permissionSets.length === 0 ? (
                            <div className="w-100 text-center">
                              <p className="ev-small-p-blue mt-5">
                                {isProcessingPopupVisible ? 'Loading Permission Sets' : 'No permission sets found'}
                              </p>
                            </div>
                          ) : (
                            permissionSets.map((set) => (
                              <tr key={set.id}>
                                <td className="align-top w-20 avatar-tooltip" data-tooltip={set.name || 'None'}>
                                  <TrimContent text={set.name} maxLength={20} />
                                </td>
                                <td className="align-top w-15">
                                  {set.permissions && set.permissions.length > 0 ? (
                                    <span className="badge bg-warning w-100">
                                      {set.permissions.length} permissions
                                    </span>
                                  ) : (
                                    <span className="ev-small-p-dark">None</span>
                                  )}
                                </td>
                                <td className="align-top w-10">
                                  <span className={`badge ${set.is_active ? 'bg-primary' : 'bg-secondary'} w-100`}>
                                    {set.is_active ? 'enabled' : 'disabled'}
                                  </span>
                                </td>
                                <td className="align-top w-15 avatar-tooltip" data-tooltip={set.creator_email || 'None'}>
                                  <div className="ev-message-card5 ev-fs-11">
                                    <TrimContent text={set.creator_email} maxLength={15} />
                                  </div>
                                </td>
                                <td className="align-top w-15">{formatDateTime(set.created_at)}</td>
                                <td className="align-top w-15">
                                  {set.updated_at ? formatDateTime(set.updated_at) : '-'}
                                </td>
                                <td className="align-top w-10">
                                  <div className="btn-group" role="group">
                                    <button 
                                      type="button"
                                      className="btn ev-btn-skyblue-fill-round ev-radius-xsmall ev-btn-med pe-2 ps-2 avatar-tooltip" 
                                      onClick={() => handleOpenEditForm(set)}
                                      data-tooltip="Edit Set"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    <button 
                                      type="button"
                                      className="btn ev-btn-red-fill-round ev-radius-xsmall ev-btn-med pe-2 ps-2 avatar-tooltip" 
                                      onClick={() => handleOpenDeleteConfirm(set)}
                                      data-tooltip="Delete Set"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div className="ev-small-p-dark">
                      Showing {permissionSets.length} of {total}
                    </div>
                    <div className="d-flex gap-2">
                      <button 
                        className="ev-btn-light-fill" 
                        disabled={page === 1}
                        onClick={() => setPage(page - 1)}
                      >
                        <i className="bi bi-chevron-left"></i> 
                      </button>
                      <button className="ev-btn-light-fill" disabled>
                        Page {page} of {Math.ceil(total / size)}
                      </button>
                      <button 
                        className="ev-btn-light-fill" 
                        disabled={page >= Math.ceil(total / size)}
                        onClick={() => setPage(page + 1)}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </div>
                    <div>
                      <PaginationControls 
                        initialSize={size}
                        onSizeChange={(newSize) => {
                          setSize(newSize);
                          setPage(1);
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Reusable Delete Confirmation Modal */}
              <DeleteConfirmationModal
                show={showDeleteConfirm}
                onClose={() => {
                  setShowDeleteConfirm(false);
                  setDeleteSet(null);
                }}
                onConfirm={handleDelete}
                objectName="permission set"
                itemName={deleteSet ? deleteSet.name : ''}
                bodyContent={
                  <div>
                    {deleteSet && deleteSet.permissions && deleteSet.permissions.length > 0 && (
                      <p className=" mt-2">
                        This permission set contains {deleteSet.permissions.length} permissions.
                      </p>
                    )}
                    <p className="text-danger mt-2">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      This action cannot be undone and will affect all users assigned to this permission set.
                    </p>
                  </div>
                }
              />
            </div>
          </main>
        </div>
      </div>
      
      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}
      
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
      
      {isSuccessPopupVisible && (
        <SuccessPopup 
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}
    </div>
  );
};

export default AdminPermissionSetManagement;